import angular from "angular";
import { Transition } from "angular-ui-router";
import { FeatureManager } from "../../../core/feature-flags/feature-manager.service";
import { configureStates, lazyLoad, RouteDeclaration } from "../../../core/router/router-helper.provider";
import { sortIcdCorporationByAccess } from "../../../core/services/data-services/icd-corporation.service";
import { V2_PAYROLL_ENTRY } from "./payroll-entry-flags";
import { PayrollEntryV2RoutingModule } from "./v2/payroll-entry-v2-routing.module";

const routes: RouteDeclaration[] = [{
    // Menu link state. This will redirect to the auto-select state. All states must descend
    // from this state in order to count the menu item link as a parent active state.
    state: "payroll.entry",
    config: {
        url: "^/payrolls",
        params: {
            showOnlyEditablePayrolls: { type: "bool", value: false }
        },
        data: {
            pageHeader: "Payroll Entry",
            permissionKey: "navigation:payrollEntry"
        },
        resolve: {
            icdCorporations: ($transition$, icdCorporationService) => {
                'ngInject';
                return icdCorporationService.retrieveIcdCorporationsByModuleAdmin("Payroll")
                    .then(icdCorporations => {
                        const params = $transition$.params();
                        return icdCorporations.filter(icdCorporation => {
                            if (params.showOnlyEditablePayrolls) {
                                return icdCorporation.Access.CanUpdate;
                            }
                            return icdCorporation.Access.CanRead;
                        })
                            .sort(sortIcdCorporationByAccess);
                    });
            },
            identity: employeeService => {
                'ngInject';
                return employeeService.retrieveIdentity();
            },
            features: (featureManager: FeatureManager) => {
                'ngInject';
                return featureManager.ensureLoaded().then(() => {
                    return {
                        v2PayrollEntry: featureManager.getFeatureFlagOption(V2_PAYROLL_ENTRY)
                    };
                })
            }
        },
        redirectTo: "payroll.entry.icdSelected.autoSelectPayroll",
        lazyLoad: lazyLoad(() => import("./payroll-entry.module").then(p => p.PayrollEntryModule))
    }
}, {
    // Legacy MVC url
    state: "payroll.entry.editPayroll",
    config: {
        url: "^/payroll/editpayroll",
        redirectTo: "payroll.entry.icdSelected.autoSelectPayroll"
    }
}, {
    // Legacy MVC url
    state: "payroll.entry.editPayroll.id",
    config: {
        url: "^/payroll/editpayroll/{payrollID:int}",
        redirectTo: "payroll.entry.edit"
    }
}, {
    state: "payroll.entry.icdSelected",
    config: {
        // Use a separate state to represent a selected ICD so that changes to select ICD don't reload the whole ICD list.
        // This state is abstract so that both auto-select and direct payroll selection can use it.
        abstract: true,
        url: "?{icdCorporationID:int}",
        resolve: {
            icdCorporationID: ($transition$, icdCorporations) => {
                'ngInject';
                // Choose the corporation based on param or first value.
                const params = $transition$.params();
                if (params.icdCorporationID) {
                    return Number(params.icdCorporationID);
                } else if (icdCorporations && icdCorporations.length) {
                    // Default to first corporation appearing in the list.
                    return icdCorporations[0].Id;
                }
                return null;
            },
            icdPayrolls: (icdCorporationID, payrollService) => {
                'ngInject';
                // Provide payrolls linked to the ICD so that the edit view
                // doesn't have to reload the data if the ICD selected is the same
                // as the ICD specified by the selected payroll.
                if (!icdCorporationID) {
                    return [];
                }
                return payrollService.retrieveIcdCorporationPayrolls(icdCorporationID)
                    .then(payrolls => ({
                        icdCorporationID,
                        payrolls: payrolls.sort((p, q) =>
                            p.StartDate > q.StartDate ? -1 : 1
                        )
                    }));
            },
            payrolls: icdPayrolls => {
                'ngInject';
                return icdPayrolls.payrolls;
            }
        }
    }

}, {
    state: "payroll.entry.icdSelected.autoSelectPayroll",
    config: {
        component: "apexPayrollEntryContainer",
        url: "?{icdCorporationID:int}",
        resolve: {
            payrollID: icdPayrolls => {
                'ngInject';
                const payrolls = icdPayrolls.payrolls;
                if (payrolls && payrolls.length) {
                    const defaultPayroll = selectDefaultPayroll(payrolls);
                    return defaultPayroll ? defaultPayroll.PayrollID : null;
                }
                return null;
            },
        },
        data: {
            skipAuthGuard: true
        },
        onEnter: function (payrollID, $state) {
            'ngInject';
            if (!payrollID) {
                // No corporation is selected, so no calculations can be loaded.
                return;
            }
            // Proceed to the edit view.
            return $state.target("payroll.entry.edit", {
                payrollID
            });
        }
    }
}, {
    state: "payroll.entry.edit",
    config: {
        url: "/{payrollID:int}/edit",
        component: "apexPayrollEntryContainer",
        resolve: {
            payrollID: ($transition$) => {
                'ngInject';
                const params = $transition$.params();
                return Number(params.payrollID);
            },
            payrolls: (identity, payrollID, icdCorporationID, payrollService) => {
                'ngInject';
                return payrollService.retrieveIcdCorporationPayrolls(icdCorporationID)
                    .then(payrolls => {
                        const payroll = payrolls.find(p => p.PayrollID === payrollID);
                        if (!payroll && identity.IsSiteAdmin) {
                            return payrollService.retrievePayrolls({
                                icdCorporation: icdCorporationID,
                                showHidden: true
                            })
                        } else {
                            return payrolls;
                        }
                    }).then(payrolls => payrolls.sort((p, q) =>
                        p.StartDate > q.StartDate ? -1 : 1
                    ));
            },
            payroll: (payrolls, payrollID) => {
                'ngInject';
                return payrolls.find(p => p.PayrollID === payrollID);
            },
            payrollCalculations: (payrollID, payrollService) => {
                'ngInject';
                return payrollService.retrievePayroll(payrollID)
                    .then(calculations => {
                        // Load aggregate information for each pay period.
                        const employees = {};
                        const payrollEntries = calculations.OwnerPayrollEntries
                            .concat(calculations.OfficeAdminPayrollEntries)
                            .concat(calculations.SalesPayrollEntries)
                            .concat(calculations.ContractorPayrollEntries);
                        for (const payrollEntry of payrollEntries) {
                            let employee = employees[payrollEntry.EmployeeID];
                            if (!employee) {
                                employee = employees[payrollEntry.EmployeeID] = {
                                    Entries: []
                                };
                            }
                            // This will add an Entries field into each payroll entry
                            // linking them to all other entries by the same employee.
                            employee.Entries.push(payrollEntry);
                            payrollEntry.Entries = employee.Entries;
                            payrollEntry.RequestManual = !payrollEntry.IsProcessedByAdp;
                            payrollEntry.Locked = payrollEntry.Locked || payrollEntry.RequestManual;
                        }
                        return calculations;
                    });
            },
            icdCorporationID: (payrollCalculations) => {
                'ngInject';
                // The selected ICD is always the ICD specified in the payroll data.
                return payrollCalculations.IcdCorporationID;
            }
        },
        redirectTo: (transition: Transition) => {
            const featureManager: FeatureManager = transition.injector().get("featureManager");
            return featureManager.ensureLoaded().then(featureManager => {
                const v2Enabled = featureManager.isEnabled(V2_PAYROLL_ENTRY);
                return v2Enabled ? "payroll.entry.edit.v2" : "payroll.entry.edit.v1";
            }) as Promise<string>;
        }
    }
}, {
    state: "payroll.entry.edit.v1",
    config: {
        component: "apexEditPayroll",
        resolve: {
            payroll: (payrolls, payrollID) => {
                'ngInject';
                return payrolls.find(p => p.PayrollID === payrollID);
            },
            allowUnlock: identity => {
                'ngInject';
                return identity.IsSiteAdmin;
            }
        },
        redirectTo: transition => {
            const featureManager: FeatureManager = transition.injector().get("featureManager");
            return featureManager.ensureLoaded().then(featureManager => {
                const v2Enabled = featureManager.isEnabled(V2_PAYROLL_ENTRY);
                if (v2Enabled) {
                    return "payroll.entry.edit.v2";
                }
            }) as Promise<string>;
        }
    }
}];

export const PayrollEntryRoutingModule = angular
    .module("apex.mainApp.payroll.entry.routing", [
        PayrollEntryV2RoutingModule
    ])
    .run(configureStates(routes))
    .name;

function selectDefaultPayroll(payWeekList: any[]) {
    const orderedPayWeekList = Array.from(payWeekList).sort(function (p, q) {
        return p.StartDate > q.StartDate ? -1 : 1;
    });

    //Get the last locked payroll
    const lastSubmittedPayroll = orderedPayWeekList.filter(p => p.Locked).sort(function (p, q) {
        return p.StartDate > q.StartDate ? -1 : 1;
    })[0];

    //Get the first unlocked payroll that is after the last locked (if any)
    const firstUnsubmittedPayroll = orderedPayWeekList.filter(function (payweek) {
        return !payweek.Locked && (!lastSubmittedPayroll || payweek.StartDate >= lastSubmittedPayroll.StartDate)
    }).sort(function (p, q) {
        return p.StartDate < q.StartDate ? -1 : 1;
    })[0];

    // Select the pay week with the given ID, or the first pay week in the list if none given.
    return firstUnsubmittedPayroll
        // Select the first pay week in the list.
        || orderedPayWeekList[0];
}
